<template>
  <div class="container" style="max-width: 700px;">
    <div style="text-align: center;">
      <img src="https://www.edisac.com/images/photolib_la/Header-com_mb.jpg" alt="EDISAC" style="max-width: 100%;" />
    </div>

    <div class="contenu">
      <!-- Chargement -->
      <p v-if="etat === 'chargement'">{{ t.chargement }}</p>

      <!-- Proposition : le client doit confirmer par un clic -->
      <template v-else-if="etat === 'ouvert'">
        <p>{{ t.bonjour }} {{ donnees.prenom }},</p>
        <p>{{ t.proposition }}</p>
        <ul>
          <li v-for="(a, i) in donnees.articles" :key="i"><b>{{ a.libelle }}</b></li>
        </ul>
        <p>{{ t.montant }} <b>{{ donnees.montant }} €</b></p>
        <p>{{ t.question }}</p>

        <div class="boutons">
          <button class="btn oui" :class="{ pre: choix === 'yes' }" :disabled="envoi" @click="repondre('yes')">
            {{ t.oui }}
          </button>
          <button class="btn non" :class="{ pre: choix === 'no' }" :disabled="envoi" @click="repondre('no')">
            {{ t.non }}
          </button>
        </div>
        <p class="note">{{ t.note }}</p>
      </template>

      <!-- Résultats -->
      <template v-else-if="['accepted', 'refused', 'getted', 'invalide', 'erreur'].includes(etat)">
        <p v-if="donnees.prenom">{{ t.bonjour }} {{ donnees.prenom }},</p>
        <p v-for="(ligne, i) in t[etat]" :key="i">{{ ligne }}</p>
        <p v-if="etat === 'erreur'">
          <button class="btn oui" @click="charger">{{ t.reessayer }}</button>
        </p>
      </template>

      <p style="margin-top: 20px;"><b>{{ t.equipe }}</b></p>
      <p class="note">
        {{ t.cgu }} <a href="https://www.edisac.com/second-life-cgv-t16037/">{{ t.ici }}</a>
      </p>
    </div>

    <img style="width: 100%; height: 10px; object-fit: cover; display: block;"
         src="https://www.edisac.com/images/photolib_la/footer2.jpg" alt="" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import store from '@/store'

const API = 'https://my.edisac.com/api/restoken'

const TEXTES = {
  FR: {
    chargement: 'Chargement de votre proposition…',
    bonjour: 'Bonjour',
    proposition: 'Suite à notre expertise, nous vous proposons de reprendre votre article :',
    montant: "Prix de reprise, sous forme d'un bon d'achat valable 6 mois dans nos magasins EDISAC :",
    question: 'Cette proposition vous convient-elle ?',
    oui: "Oui, j'accepte", non: 'Non, je refuse',
    note: 'Votre réponse est définitive.',
    accepted: [
      'Nous sommes ravis que notre offre vous ait convaincu !',
      "Vous allez bientôt recevoir un e-mail contenant votre bon d'achat, valable dans tous nos magasins pendant 6 mois.",
      'Votre article va désormais être reconditionné avec soin par notre équipe, puis remis en vente afin de lui offrir une seconde vie.',
    ],
    refused: [
      "Nous sommes navrés que notre proposition n'ait pas retenu votre attention.",
      'Pas de souci, nous respectons totalement votre choix !',
      "Selon l'option choisie lors du dépôt, votre article sera soit renvoyé en magasin pour vous être restitué, soit donné à notre association partenaire.",
    ],
    getted: ['Nous avons déjà reçu votre réponse.', "Consultez vos e-mails ou contactez l'équipe EDISAC."],
    invalide: ["Ce lien n'est pas valide ou a expiré.", "Contactez l'équipe EDISAC si besoin."],
    erreur: ["Une erreur est survenue, votre réponse n'a pas pu être lue."],
    reessayer: 'Réessayer',
    equipe: "L'équipe EDISAC",
    cgu: "Retrouvez nos conditions générales d'utilisation", ici: 'ici',
  },
  NL: {
    chargement: 'Uw voorstel wordt geladen…',
    bonjour: 'Beste',
    proposition: 'Na onze beoordeling stellen wij voor uw artikel over te nemen:',
    montant: 'Overnameprijs, in de vorm van een waardebon die 6 maanden geldig is in onze EDISAC-winkels:',
    question: 'Gaat u akkoord met dit voorstel?',
    oui: 'Ja, ik ga akkoord', non: 'Nee, ik weiger',
    note: 'Uw antwoord is definitief.',
    accepted: [
      'We zijn verheugd dat onze aanbieding u heeft overtuigd!',
      'U ontvangt binnenkort een e-mail met uw waardebon, 6 maanden geldig in al onze winkels.',
      'Uw artikel wordt nu zorgvuldig gereconditioneerd en weer te koop aangeboden voor een tweede leven.',
    ],
    refused: [
      'We betreuren dat ons voorstel u niet heeft overtuigd.',
      'Geen probleem, we respecteren uw keuze volledig!',
      'Afhankelijk van uw keuze bij het inleveren wordt uw artikel teruggestuurd naar de winkel of geschonken aan onze partnervereniging.',
    ],
    getted: ['We hebben uw antwoord al ontvangen.', 'Raadpleeg uw e-mails of neem contact op met het EDISAC-team.'],
    invalide: ['Deze link is ongeldig of verlopen.', 'Neem indien nodig contact op met het EDISAC-team.'],
    erreur: ['Er is een fout opgetreden, uw antwoord kon niet worden gelezen.'],
    reessayer: 'Opnieuw proberen',
    equipe: 'Het EDISAC-team',
    cgu: 'Onze algemene voorwaarden vindt u', ici: 'hier',
  },
}

const route = useRoute()
const etat = ref('chargement') // chargement | ouvert | accepted | refused | getted | invalide | erreur
const donnees = ref({})
const envoi = ref(false)

const token = computed(() => String(route.query.token || ''))
const choix = computed(() => route.query.res) // choix cliqué dans l'e-mail : seulement mis en avant
const t = computed(() => TEXTES[donnees.value.language === 'NL' ? 'NL' : 'FR'])

async function appeler(corps) {
  const { data } = await axios.post(API, corps)
  donnees.value = { ...donnees.value, ...data }
  etat.value = data.statut || 'erreur'
}

// Consultation uniquement : rien n'est enregistré à l'ouverture de la page
async function charger() {
  if (!token.value) { etat.value = 'invalide'; return }
  etat.value = 'chargement'
  try {
    await appeler({ token: token.value })
  } catch (e) {
    etat.value = e.response?.status === 400 ? 'invalide' : 'erreur'
  }
}

// Enregistrement de la réponse, seulement après un clic sur un bouton
async function repondre(res) {
  envoi.value = true
  try {
    await appeler({ token: token.value, res })
  } catch (e) {
    etat.value = 'erreur'
  } finally {
    envoi.value = false
  }
}

onMounted(() => {
  store.commit('addUser', { groupe: 'N' }) // comme avant : profil « client » pour le reste de l'application
  charger()
})
watch(token, charger)
</script>

<style scoped>
.contenu { font-family: Arial, Helvetica, sans-serif; font-size: 1rem; padding: 20px 10px; line-height: 1.5; }
.contenu p { margin: 0 0 12px; }
.boutons { display: flex; gap: 12px; justify-content: center; margin: 20px 0 8px; flex-wrap: wrap; }
.btn { padding: 10px 24px; border-radius: 6px; border: 2px solid transparent; font-weight: bold; cursor: pointer; font-size: 1rem; }
.btn:disabled { opacity: 0.6; cursor: wait; }
.oui { background: #1a2a4a; color: #fff; }
.non { background: #fff; color: #1a2a4a; border-color: #1a2a4a; }
.pre { box-shadow: 0 0 0 3px #f0b429; }
.note { font-size: 0.85rem; color: #666; text-align: center; }
</style>
